import Switch from '../ui/Switch'
import Select from '../ui/Select'
import { useState } from 'react'

import type { ColumnInfo } from '../api/client'
import { formatNumberValue, readNumFormat, writeNumFormat } from './formatNumber'

export interface NumberFormatDialogProps {
  column: ColumnInfo
  busy: boolean
  onCancel: () => void
  onSave: (settingsJson: string) => void
}

/**
 * 「显示格式」弹框（backlog #7）。
 *
 * 只给公式列用：千分位 / 百分比 / 小数位三项，存列 settings.numFormat（落库）。
 * 右上角有实时预览，避免用户对百分比语义产生误解。
 */
export default function NumberFormatDialog({
  column,
  busy,
  onCancel,
  onSave,
}: NumberFormatDialogProps) {
  const initial = readNumFormat(column.settings)
  const [thousands, setThousands] = useState(initial.thousands === true)
  const [percent, setPercent] = useState(initial.percent === true)
  const [decimals, setDecimals] = useState<number>(initial.decimals ?? -1)

  const fmt = {
    thousands,
    percent,
    decimals: decimals < 0 ? undefined : decimals,
  }

  const previewRaw = '1234567.891'
  const previewSmall = '0.5'

  return (
    <div className="dialog-mask" onClick={(e) => { if (e.target === e.currentTarget) onCancel() }}>
      <div className="dialog" role="dialog" aria-modal="true" aria-label="显示格式">
        <header className="dialog__head">
          <span className="dialog__title">显示格式 · {column.title}</span>
        </header>

        <div className="dialog__body">
          <div className="fmt-row fmt-row--switch">
            <Switch checked={thousands} onChange={setThousands} label="千分位" />
            <span>千分位</span>
            <span className="dim">1234567 → 1,234,567</span>
          </div>

          <div className="fmt-row fmt-row--switch">
            <Switch checked={percent} onChange={setPercent} label="百分比" />
            <span>百分比</span>
            <span className="dim">数值 × 100 后加 %（0.5 → 50%）；公式本身已经算成百分数就不要勾</span>
          </div>

          <label className="fmt-row">
            <span>小数位</span>
            <Select
              className="cc-select"
              value={decimals}
              onChange={(v) => setDecimals(Number(v))}
            >
              <option value={-1}>不固定（保留原值）</option>
              <option value={0}>0</option>
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
            </Select>
          </label>

          <p className="dialog__note">
            预览：{formatNumberValue(previewRaw, fmt)}
            {percent ? `　（0.5 → ${formatNumberValue(previewSmall, fmt)}）` : ''}
          </p>
        </div>

        <footer className="dialog__foot">
          <button type="button" className="dialog__btn" onClick={onCancel} disabled={busy}>
            取消
          </button>
          <button
            type="button"
            className="dialog__btn dialog__btn--primary"
            disabled={busy}
            onClick={() => onSave(writeNumFormat(column.settings, fmt))}
          >
            保存
          </button>
        </footer>
      </div>
    </div>
  )
}
